import { useQuery } from "@tanstack/react-query";
import type { CoReviewComment } from "../../../shared/interfaces/CoReviewComment";
import { coCommentsQuery, type CoCommentsCycle } from "../api/coReviewQueries";

const EMPTY: CoReviewComment[] = [];

export interface UseCoCommentsOptions {
  reportId: string | undefined;
  cycle: CoCommentsCycle;
  enabled?: boolean;
}

/**
 * The one sanctioned reader of the CO-comments cache. Consumers get a plain
 * array and never touch query.data directly, which defuses two react-query v5
 * traps that have produced real blockers in this codebase:
 *
 * - A mounted-but-DISABLED observer pins data past gcTime 0. When the edit
 *   page's enabled flag flips off mid-session (status leaves
 *   CHANGES_REQUESTED), consumers must see [], not the pinned stale list.
 * - The error dispatch RETAINS previous data, so error must be checked
 *   before data or the failure branch is unreachable after any success.
 */
export function useCoComments({ reportId, cycle, enabled }: UseCoCommentsOptions) {
  const active = (enabled ?? true) && !!reportId;
  const query = useQuery({
    ...coCommentsQuery(reportId ?? "", cycle),
    enabled: active,
  });

  const comments: CoReviewComment[] =
    !active || query.isError ? EMPTY : (query.data ?? EMPTY);

  return {
    comments,
    error: active ? (query.error ?? null) : null,
    isLoading: active && query.isLoading,
    dataUpdatedAt: query.dataUpdatedAt,
  };
}
